import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Search, Plus, Ban, Trash2, Gift, X, Pencil, History, RotateCcw, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { validatePassword } from "../../lib/passwordPolicy";
import PasswordRules from "../../components/PasswordRules";
import ConfirmDialog from "../../components/ConfirmDialog";
import HistoryModal from "../../components/admin/HistoryModal";

const STATUS = { active: "active", blocked: "blocked" };

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active");
  const [showCreate, setShowCreate] = useState(false);
  const [grantUser, setGrantUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  // Spec fork 24 — modal historique du visiteur sélectionné + confirmation reset.
  const [historyUser, setHistoryUser] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  // Spec fork 25.2 — purge en masse + filtre actifs uniquement.
  const [activeOnly, setActiveOnly] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      if (activeOnly) params.set("hasActiveTicket", "true");
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data.items || []);
    } catch { setUsers([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, activeOnly]);

  const toggleBlock = async (u) => {
    if (u.isAdmin) { toast.error("Un administrateur ne peut pas être bloqué"); return; }
    try {
      await api.patch(`/admin/users/${u.id}`, { isBlocked: !u.isBlocked });
      toast.success(u.isBlocked ? "Visiteur débloqué" : "Visiteur bloqué");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const hardDelete = (u) => {
    if (u.isAdmin) { toast.error("Un administrateur ne peut pas être supprimé"); return; }
    setDeleteTarget(u);
  };

  const performHardDelete = async () => {
    const u = deleteTarget;
    setDeleteTarget(null);
    if (!u) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success(`Compte supprimé. L'email ${u.email} est libéré.`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!users) return <div className="text-subtle">Chargement…</div>;

  return (
    <div data-testid="admin-users">
      <div className="flex items-end justify-between gap-4 flex-wrap mb-4">
        <h2 className="font-display text-2xl font-black text-ink">Visiteurs</h2>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setBulkOpen(true)} size="sm" variant="outline" className="border-destructive text-destructive hover:bg-destructive/5 rounded-md" data-testid="users-bulk-delete-btn">
            <UserMinus size={14} className="mr-1" /> Tout supprimer (sauf admin)
          </Button>
          <Button onClick={() => setResetOpen(true)} size="sm" variant="outline" className="border-destructive text-destructive hover:bg-destructive/5 rounded-md" data-testid="users-reset-history-btn">
            <RotateCcw size={14} className="mr-1" /> Réinitialiser l'historique
          </Button>
          <Button onClick={() => setShowCreate(true)} size="sm" className="bg-brand hover:bg-brand-hover text-white rounded-md" data-testid="users-create-btn">
            <Plus size={14} className="mr-1" /> Créer un visiteur
          </Button>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-subtle" size={14} />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher email, nom..." className="pl-9 h-10 w-64" data-testid="users-search-input" />
          </div>
          <Button type="submit" variant="outline" size="sm" className="h-10 border border-ink rounded-md">Rechercher</Button>
        </form>
        <label className="inline-flex items-center gap-2 text-sm h-10 px-3 border border-border rounded-md bg-white cursor-pointer hover:border-ink" data-testid="users-active-only-toggle">
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
            className="accent-brand"
          />
          <span className="font-bold text-xs uppercase tracking-wider">Avec ticket actif</span>
        </label>
        <div className="flex gap-1 ml-auto">
          {Object.entries(STATUS).map(([k]) => (
            <button
              key={k}
              onClick={() => setStatus(k)}
              className={`px-3 h-10 text-xs font-bold uppercase tracking-wider border ${status === k ? "bg-ink text-white border-ink" : "bg-white text-ink border-border"}`}
              data-testid={`users-filter-${k}`}
            >
              {k === "active" ? "Actifs" : "Bloqués"}
            </button>
          ))}
        </div>
      </div>

      <div className="border border-border overflow-x-auto hidden md:block">
        <table className="w-full text-sm">
          <thead className="bg-surface/60 text-subtle text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-3">Nom</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Inscription</th>
              <th className="text-left p-3">Statut</th>
              <th className="text-left p-3">Accès gratuit</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-subtle">Aucun visiteur</td></tr>
            )}
            {users.map((u) => (
              <tr key={u.id} className="border-t border-border" data-testid={`user-row-${u.id}`}>
                <td className="p-3 text-ink">
                  {u.prenom} {u.nom}
                  {u.isAdmin && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-brand text-white px-1.5 py-0.5">Admin</span>}
                </td>
                <td className="p-3 text-ink font-mono text-xs">{u.email}</td>
                <td className="p-3 text-subtle">{new Date(u.createdAt).toLocaleDateString("fr-FR")}</td>
                <td className="p-3">
                  {u.isBlocked ? <Badge variant="blocked" /> : <Badge variant="active" />}
                </td>
                <td className="p-3 text-xs text-subtle">
                  {u.freeTicketsLeft ? <span>{u.freeTicketsLeft} tickets</span> : null}
                  {u.freeTicketsLeft && u.freeAccessUntil ? <br /> : null}
                  {u.freeAccessUntil ? <span>jusqu'au {new Date(u.freeAccessUntil).toLocaleDateString("fr-FR")}</span> : null}
                  {!u.freeTicketsLeft && !u.freeAccessUntil ? "—" : null}
                </td>
                <td className="p-3 text-right">
                  <div className="inline-flex gap-1">
                    <IconBtn onClick={() => setHistoryUser(u)} title="Voir l'historique" icon={<History size={12} />} testid={`user-history-${u.id}`} />
                    <IconBtn onClick={() => setEditUser(u)} title="Modifier" icon={<Pencil size={12} />} testid={`user-edit-${u.id}`} />
                    {!u.isAdmin && (
                      <>
                        <IconBtn onClick={() => setGrantUser(u)} title="Accès gratuit" icon={<Gift size={12} />} testid={`user-grant-${u.id}`} />
                        <IconBtn onClick={() => toggleBlock(u)} title={u.isBlocked ? "Débloquer" : "Bloquer"} icon={<Ban size={12} className={u.isBlocked ? "text-destructive" : ""} />} testid={`user-block-${u.id}`} />
                        <IconBtn onClick={() => hardDelete(u)} title="Supprimer définitivement" icon={<Trash2 size={12} className="text-destructive" />} testid={`user-delete-${u.id}`} />
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* MOBILE : liste de cartes empilées (md:hidden) — reprend les mêmes
         actions que les lignes desktop. */}
      <div className="md:hidden space-y-2" data-testid="users-cards-mobile">
        {users.length === 0 && (
          <div className="border border-border p-6 text-center text-subtle text-sm">Aucun visiteur</div>
        )}
        {users.map((u) => (
          <div key={u.id} className="border border-border bg-white p-3" data-testid={`user-card-${u.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <p className="font-bold text-ink truncate">
                  {u.prenom} {u.nom}
                  {u.isAdmin && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-brand text-white px-1.5 py-0.5 rounded">Admin</span>}
                </p>
                <p className="text-xs font-mono text-subtle truncate">{u.email}</p>
              </div>
              {u.isBlocked ? <Badge variant="blocked" /> : <Badge variant="active" />}
            </div>
            <div className="text-[11px] text-subtle space-y-0.5 mb-2">
              <p>Inscrit le {new Date(u.createdAt).toLocaleDateString("fr-FR")}</p>
              {(u.freeTicketsLeft || u.freeAccessUntil) && (
                <p>
                  Accès gratuit :{" "}
                  {u.freeTicketsLeft ? <span>{u.freeTicketsLeft} tickets </span> : null}
                  {u.freeAccessUntil ? <span>jusqu'au {new Date(u.freeAccessUntil).toLocaleDateString("fr-FR")}</span> : null}
                </p>
              )}
            </div>
            <div className="flex gap-1 flex-wrap pt-2 border-t border-border">
              <IconBtn onClick={() => setHistoryUser(u)} title="Voir l'historique" icon={<History size={12} />} testid={`user-history-m-${u.id}`} />
              <IconBtn onClick={() => setEditUser(u)} title="Modifier" icon={<Pencil size={12} />} testid={`user-edit-m-${u.id}`} />
              {!u.isAdmin && (
                <>
                  <IconBtn onClick={() => setGrantUser(u)} title="Accès gratuit" icon={<Gift size={12} />} testid={`user-grant-m-${u.id}`} />
                  <IconBtn onClick={() => toggleBlock(u)} title={u.isBlocked ? "Débloquer" : "Bloquer"} icon={<Ban size={12} className={u.isBlocked ? "text-destructive" : ""} />} testid={`user-block-m-${u.id}`} />
                  <IconBtn onClick={() => hardDelete(u)} title="Supprimer définitivement" icon={<Trash2 size={12} className="text-destructive" />} testid={`user-delete-m-${u.id}`} />
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {showCreate && <CreateUserModal onClose={() => setShowCreate(false)} onDone={() => { setShowCreate(false); load(); }} />}
      {grantUser && <GrantModal user={grantUser} onClose={() => setGrantUser(null)} onDone={() => { setGrantUser(null); load(); }} />}
      {editUser && <EditUserModal user={editUser} onClose={() => setEditUser(null)} onDone={() => { setEditUser(null); load(); }} />}

      <ConfirmDialog
        open={!!deleteTarget}
        title={deleteTarget ? `Supprimer ${deleteTarget.prenom} ${deleteTarget.nom} ?` : "Supprimer"}
        message={deleteTarget ? (
          <>
            <p>Le compte sera <strong>définitivement effacé</strong>.</p>
            <p>L'email <strong>{deleteTarget.email}</strong> sera libéré et pourra être réutilisé.</p>
            <p className="text-destructive font-bold">Cette action est irréversible.</p>
          </>
        ) : null}
        confirmLabel="Supprimer"
        destructive
        onConfirm={performHardDelete}
        onCancel={() => setDeleteTarget(null)}
        testid="user-delete-confirm"
      />

      {historyUser && (
        <HistoryModal
          kind="user"
          entityId={historyUser.id}
          title={`${historyUser.prenom || ""} ${historyUser.nom || ""}`.trim() + ` (${historyUser.email || "—"})`}
          onClose={() => setHistoryUser(null)}
        />
      )}

      <ConfirmDialog
        open={resetOpen}
        title="Réinitialiser l'historique des visiteurs ?"
        message={(
          <>
            <p>Toutes les lignes d'historique (tickets validés, annulés, absents…) seront <strong>supprimées définitivement</strong>.</p>
            <p>Les comptes visiteurs et les tickets actifs (en attente, appelés) <strong>ne sont PAS touchés</strong>.</p>
            <p className="text-destructive font-bold">Cette action est irréversible.</p>
          </>
        )}
        confirmLabel={resetting ? "Réinitialisation…" : "Réinitialiser"}
        destructive
        onConfirm={async () => {
          setResetting(true);
          try {
            const { data } = await api.post("/admin/history/reset", { scope: "users" });
            toast.success(`Historique réinitialisé (${data?.deleted?.tickets || 0} lignes supprimées)`);
            setResetOpen(false);
            load();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
          } finally {
            setResetting(false);
          }
        }}
        onCancel={() => setResetOpen(false)}
        testid="users-reset-confirm"
      />

      <ConfirmDialog
        open={bulkOpen}
        title="Supprimer tous les visiteurs (sauf admin) ?"
        message={(
          <>
            <p>Tous les comptes visiteurs (invités fantômes inclus) seront <strong>définitivement supprimés</strong>.</p>
            <p>Les comptes administrateurs sont <strong>préservés</strong>.</p>
            <p>Les tickets encore en attente / appelés des comptes supprimés seront marqués absents.</p>
            <p className="text-destructive font-bold">Cette action est irréversible.</p>
          </>
        )}
        confirmLabel={bulkDeleting ? "Suppression…" : "Tout supprimer"}
        destructive
        onConfirm={async () => {
          setBulkDeleting(true);
          try {
            const { data } = await api.post("/admin/users/bulk-delete", { scope: "all_non_admin" });
            toast.success(`${data?.deleted?.users || 0} compte(s) supprimé(s)`);
            setBulkOpen(false);
            load();
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur");
          } finally {
            setBulkDeleting(false);
          }
        }}
        onCancel={() => setBulkOpen(false)}
        testid="users-bulk-delete-confirm"
      />
    </div>
  );
}

function IconBtn({ onClick, title, icon, testid }) {
  return <button onClick={onClick} title={title} data-testid={testid} className="h-7 w-7 border border-border inline-flex items-center justify-center hover:border-ink bg-white">{icon}</button>;
}

function Badge({ variant }) {
  const map = {
    active: { label: "Actif", cls: "bg-[#ECFBEF] text-[#0A7F2E]" },
    blocked: { label: "Bloqué", cls: "bg-[#FDE2E2] text-destructive" },
  };
  const v = map[variant];
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${v.cls}`}>{v.label}</span>;
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-md w-full mt-20" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-display text-lg font-bold text-ink">{title}</h3>
          <button onClick={onClose} className="text-subtle hover:text-ink"><X size={16} /></button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function CreateUserModal({ onClose, onDone }) {
  const [form, setForm] = useState({ prenom: "", nom: "", email: "", password: "", isAdmin: false });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password) {
      const v = validatePassword(form.password);
      if (!v.ok) { toast.error(v.message); return; }
    }
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", form);
      toast.success(data.generatedPassword ? `Créé. Mot de passe temporaire : ${data.generatedPassword}` : "Visiteur créé", { duration: 8000 });
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Créer un visiteur" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="admin-user-create-form">
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Prénom</Label><Input required value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} /></div>
          <div><Label>Nom</Label><Input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
        </div>
        <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-user-create-email" /></div>
        <div>
          <Label>Mot de passe (vide = généré aléatoirement)</Label>
          <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {form.password && <PasswordRules password={form.password} compact />}
        </div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.isAdmin} onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })} /> Donner les droits administrateur</label>
        <Button type="submit" disabled={saving} className="w-full h-11 bg-brand text-white rounded-md" data-testid="admin-user-create-submit">Créer</Button>
      </form>
    </Modal>
  );
}

function GrantModal({ user, onClose, onDone }) {
  const [tickets, setTickets] = useState(user.freeTicketsLeft ?? "");
  const [until, setUntil] = useState(user.freeAccessUntil ? user.freeAccessUntil.slice(0, 10) : "");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const body = {
        freeTicketsLeft: tickets === "" ? null : Number(tickets),
        freeAccessUntil: until ? new Date(until + "T23:59:59").toISOString() : null,
      };
      await api.patch(`/admin/users/${user.id}`, body);
      toast.success("Accès gratuit mis à jour");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  const unlimited = () => setUntil("2099-12-31");
  const clear = () => { setTickets(""); setUntil(""); };
  return (
    <Modal title={`Accès gratuit · ${user.prenom} ${user.nom}`} onClose={onClose}>
      <div className="space-y-3">
        <p className="text-xs text-subtle leading-relaxed">
          Vous pouvez offrir <strong>X tickets prioritaires gratuits</strong> (compteur), une <strong>date de validité</strong> (gratuit jusqu'au...), ou les deux à la fois. Bouton "Illimité" → expire en 2099.
        </p>
        <div><Label>Tickets gratuits (compteur)</Label><Input type="number" min={0} value={tickets} onChange={(e) => setTickets(e.target.value)} placeholder="Ex : 10" data-testid="grant-tickets-input" /></div>
        <div><Label>Gratuit jusqu'au</Label><Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} data-testid="grant-until-input" /></div>
        <div className="flex gap-2 flex-wrap">
          <Button type="button" size="sm" variant="outline" onClick={unlimited}>Illimité</Button>
          <Button type="button" size="sm" variant="outline" onClick={clear}>Retirer</Button>
        </div>
        <Button onClick={submit} disabled={saving} className="w-full h-11 bg-brand text-white rounded-md" data-testid="grant-submit">Enregistrer</Button>
      </div>
    </Modal>
  );
}

function EditUserModal({ user, onClose, onDone }) {
  const [form, setForm] = useState({ prenom: user.prenom, nom: user.nom, email: user.email, password: "" });
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (form.password) {
      const v = validatePassword(form.password);
      if (!v.ok) { toast.error(v.message); return; }
    }
    setSaving(true);
    try {
      const body = { prenom: form.prenom, nom: form.nom, email: form.email };
      if (form.password) body.password = form.password;
      await api.patch(`/admin/users/${user.id}`, body);
      toast.success("Visiteur mis à jour");
      onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Modifier le visiteur" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <div><Label>Prénom</Label><Input required value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} /></div>
          <div><Label>Nom</Label><Input required value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} /></div>
        </div>
        <div><Label>Email</Label><Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div>
          <Label>Nouveau mot de passe (laisser vide pour inchangé)</Label>
          <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          {form.password && <PasswordRules password={form.password} compact />}
        </div>
        <Button type="submit" disabled={saving} className="w-full h-11 bg-brand text-white rounded-md">Enregistrer</Button>
      </form>
    </Modal>
  );
}
